/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

import IDisposable from '../lang/IDisposable';
import Disposable from '../lang/Disposable';
import Subject from '../rx/Subject';
import DisposableList from '../lang/DisposableList';

export default class NetworkMonitor implements IDisposable {
  private readonly _disposables: DisposableList = new DisposableList();
  private readonly _isSupported = navigator && navigator['connection'];
  private readonly _rtt: Subject<number> = new Subject(undefined);
  private readonly _effectiveType: Subject<string> = new Subject(undefined);
  private readonly _downlinkThroughputCapacity: Subject<number> = new Subject(undefined);

  constructor() {
    if (!this.isSupported) {
      return;
    }

    this.setupNetworkChangeListeners();
    this.updateStatistics();
  }

  get isSupported(): boolean {
    return this._isSupported;
  }

  get rtt(): Subject<number> {
    return this._rtt;
  }

  get effectiveType(): Subject<string> {
    return this._effectiveType;
  }

  get downlinkThroughputCapacity(): Subject<number> {
    return this._downlinkThroughputCapacity;
  }

  dispose(): void {
    this._disposables.dispose();
  }

  private getDownlinkThroughputCapacity(): number {
    if (!this.isSupported) {
      return -1;
    }

    return navigator['connection'].downlink || navigator['connection'].downlinkMax;
  }

  private getEffectiveType(): string {
    if (!this.isSupported) {
      return 'Unknown';
    }

    return navigator['connection'].effectiveType || navigator['connection'].type;
  }

  private getRoundTripTime(): number {
    return navigator['connection'].rtt;
  }

  private setupNetworkChangeListeners(): void {
    const listener = (): void => {
      this.updateStatistics();
    };

    navigator['connection'].addEventListener('change', listener);

    this._disposables.add(new Disposable(() => {
      navigator['connection'].removeEventListener('change', listener);
    }));
  }

  private updateStatistics(): void {
    this._rtt.value = this.getRoundTripTime();
    this._effectiveType.value = this.getEffectiveType();
    this._downlinkThroughputCapacity.value = this.getDownlinkThroughputCapacity();
  }
}